import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { MenuSection } from "../components/MenuSection";
import { RestaurantChangeDialog } from "../components/RestaurantChangeDialog";
import { usePageMetadata } from "../hooks/usePageMetadata";
import PublicPageLayout from "./PublicPageLayout";

export default function RestaurantMenu() {
  const { restaurantSlug } = useParams();
  const navigate = useNavigate();
  const { cart, cartRestaurant, clear } = useStore();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState({});
  const [error, setError] = useState("");
  const [switchOpen, setSwitchOpen] = useState(false);
  usePageMetadata({
    title: profile ? `Order from ${profile.display_name} | Lucky's Kitchen` : error ? "Restaurant unavailable | Lucky's Kitchen" : "Loading menu | Lucky's Kitchen",
    canonicalPath: `/order/${restaurantSlug}`,
    noIndex: Boolean(error),
  });

  useEffect(() => {
    setData(null); setProfile(null); setError("");
    Promise.all([
      api.get(`/restaurants/${restaurantSlug}`),
      api.get(`/restaurants/${restaurantSlug}/menu`),
      api.get(`/restaurants/${restaurantSlug}/reviews/summary`).catch(() => ({ data: {} })),
    ]).then(([profileResponse, menuResponse, ratingsResponse]) => {
      setProfile(profileResponse.data);
      setData(menuResponse.data);
      setRatings(ratingsResponse.data);
    }).catch((e) => {
      setError(e?.response?.status === 404 ? "This restaurant is not available for online ordering." : errMsg(e));
    });
  }, [restaurantSlug]);

  useEffect(() => {
    if (profile && cart.length && cartRestaurant.id !== profile.id) setSwitchOpen(true);
  }, [profile, cart.length, cartRestaurant.id]);

  const changeRestaurant = () => {
    if (cart.length) setSwitchOpen(true);
    else navigate("/order");
  };

  if (error) return (
    <PublicPageLayout><section className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="restaurant-not-found"><h1 className="font-serif text-3xl font-bold text-brand-ink">Restaurant unavailable</h1><p className="mt-4 text-brand-mute">{error}</p><Link to="/order" className="mt-7 inline-block rounded-full bg-brand-red px-6 py-3 font-bold text-brand-cream">Choose a restaurant</Link></section></PublicPageLayout>
  );

  return (
    <PublicPageLayout>
      <div className="min-h-screen bg-brand-cream" data-testid="restaurant-menu-page">
      {!profile || !data ? <p className="py-24 text-center text-brand-mute" role="status">Loading menu…</p> : <>
        <section className="bg-brand-brown px-4 py-10 text-brand-cream sm:px-8">
          <div className="mx-auto max-w-7xl">
            <button onClick={changeRestaurant} className="inline-flex items-center gap-2 text-sm font-bold text-brand-gold underline" data-testid="change-restaurant-button"><ArrowLeft className="h-4 w-4" />Change restaurant</button>
            <h1 className="mt-5 font-serif text-4xl font-bold sm:text-5xl">{profile.display_name}</h1>
            <p className="mt-3 flex max-w-2xl gap-2 text-sm leading-relaxed text-brand-cream/75"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />{profile.address}</p>
            <p className="mt-2 text-sm text-brand-cream/75">{profile.hours_summary}</p>
          </div>
        </section>
        <MenuSection categories={data.categories} items={data.items} ratings={ratings} restaurant={profile} />
      </>}
      <RestaurantChangeDialog
        open={switchOpen}
        onOpenChange={setSwitchOpen}
        restaurantName={cartRestaurant.display_name}
        onCancel={() => {
          if (profile && cartRestaurant.id !== profile.id) navigate(`/order/${cartRestaurant.slug}`, { replace: true });
        }}
        onConfirm={() => { clear(); setSwitchOpen(false); if (profile && cartRestaurant.id === profile.id) navigate("/order"); }}
      />
      </div>
    </PublicPageLayout>
  );
}

